/**
 * Meal Builder Enhanced Functionality
 * Extends the basic functionality with additional features
 */

class MealBuilder {
  constructor() {
    this.loadingMore = false;
    this.init();
  }

  init() {
    this.setupLocalStorage();
    this.setupAnalytics();
    this.setupKeyboardNavigation();
    this.setupTooltips();
    this.setupPaginationObserver();
  }

  setupLocalStorage() {
    // Save user preferences to localStorage
    const savedTarget = localStorage.getItem('mealBuilderTarget');
    const savedDelivery = localStorage.getItem('mealBuilderDelivery');
    const savedMealType = localStorage.getItem('mealBuilderMealType');

    if (savedTarget) {
      const targetChip = document.querySelector(`[data-count="${savedTarget}"]`);
      if (targetChip) {
        document.querySelectorAll('.meal-count-chip').forEach(chip => chip.classList.remove('active'));
        targetChip.classList.add('active');
      }
    }

    if (savedDelivery) {
      const deliveryOption = document.querySelector(`input[value="${savedDelivery}"]`);
      if (deliveryOption) {
        deliveryOption.checked = true;
        deliveryOption.closest('.delivery-option').classList.add('active');
      }
    }

    if (savedMealType) {
      const mealTypeFilter = document.querySelector(`[data-meal-type="${savedMealType}"]`);
      if (mealTypeFilter) {
        document.querySelectorAll('.meal-type-filter').forEach(filter => filter.classList.remove('active'));
        mealTypeFilter.classList.add('active');
      }
    }

    // Save preferences on change
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('meal-count-chip')) {
        localStorage.setItem('mealBuilderTarget', e.target.dataset.count);
      }
      if (e.target.classList.contains('meal-type-filter')) {
        localStorage.setItem('mealBuilderMealType', e.target.dataset.mealType);
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target.name === 'delivery_day') {
        localStorage.setItem('mealBuilderDelivery', e.target.value);
      }
    });
  }

  setupAnalytics() {
    // Track meal builder interactions
    const trackEvent = (event, properties) => {
      if (typeof gtag !== 'undefined') {
        gtag('event', event, properties);
      }
      if (typeof fbq !== 'undefined') {
        fbq('track', event, properties);
      }
      if (window.ShopifyAnalytics && typeof window.ShopifyAnalytics.trackCustomEvent === 'function') {
        window.ShopifyAnalytics.trackCustomEvent(event, properties);
      }
    };

    // Track meal additions
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('add-meal-button')) {
        const card = e.target.closest('.meal-card');
        const productId = card?.dataset.productId;
        const productTitle = card?.querySelector('.meal-title')?.textContent;
        const isPackage = e.target.dataset.isPackage;
        const isByPound = e.target.dataset.isByPound;

        let itemType = 'meal';
        if (isPackage) itemType = 'package';
        if (isByPound) itemType = 'by_pound';

        trackEvent('add_to_cart_meal_builder', {
          product_id: productId,
          product_name: productTitle,
          item_type: itemType,
          source: 'meal_builder'
        });
      }
    });

    // Track target changes
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('meal-count-chip')) {
        trackEvent('meal_target_changed', {
          new_target: e.target.dataset.count
        });
      }
    });

    // Track checkout attempts with new logic
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('checkout-button') || e.target.classList.contains('mobile-checkout-btn')) {
        const cartData = this.getCartData();
        const mealCount = this.getMealCount(cartData);
        const packageCount = this.getPackageCount(cartData);
        const byPoundCount = this.getByPoundCount(cartData);

        trackEvent('begin_checkout', {
          source: 'meal_builder',
          value: this.getCartTotal(),
          meal_count: mealCount,
          package_count: packageCount,
          by_pound_count: byPoundCount,
          checkout_type: mealCount > 0 ? 'meal_prep_required' : 'packages_only'
        });
      }
    });
  }

  setupKeyboardNavigation() {
    // Enhanced keyboard navigation for accessibility
    document.addEventListener('keydown', (e) => {
      const focusedElement = document.activeElement;

      // Arrow key navigation for chips and filters
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        let siblings = [];
        let currentIndex = -1;

        if (focusedElement.classList.contains('meal-count-chip')) {
          siblings = Array.from(document.querySelectorAll('.meal-count-chip'));
        } else if (focusedElement.classList.contains('meal-type-filter')) {
          siblings = Array.from(document.querySelectorAll('.meal-type-filter'));
        }

        if (siblings.length > 0) {
          currentIndex = siblings.indexOf(focusedElement);
          const nextIndex = e.key === 'ArrowRight'
            ? (currentIndex + 1) % siblings.length
            : (currentIndex - 1 + siblings.length) % siblings.length;

          siblings[nextIndex].focus();
          e.preventDefault();
        }
      }

      // Enter key activation
      if (e.key === 'Enter' && focusedElement.classList.contains('meal-count-chip')) {
        focusedElement.click();
        e.preventDefault();
      }
    });
  }

  setupTooltips() {
    // Simple tooltip system for help text
    const tooltipElements = document.querySelectorAll('[data-tooltip]');

    tooltipElements.forEach(element => {
      element.addEventListener('mouseenter', (e) => {
        this.showTooltip(e.target, e.target.dataset.tooltip);
      });

      element.addEventListener('mouseleave', () => {
        this.hideTooltip();
      });
    });
  }

  showTooltip(element, text) {
    const tooltip = document.createElement('div');
    tooltip.className = 'meal-builder-tooltip';
    tooltip.textContent = text;
    tooltip.style.cssText = `
      position: fixed;
      background: var(--ink);
      color: var(--white);
      padding: 8px 12px;
      border-radius: 6px;
      font-size: 12px;
      z-index: 1000;
      white-space: nowrap;
      opacity: 0;
      transition: opacity 0.2s ease;
    `;

    document.body.appendChild(tooltip);

    const rect = element.getBoundingClientRect();
    // Centre above element using CSS transform so we don't need offsetWidth
    // (which is 0 before the browser has painted the newly-appended element)
    tooltip.style.left = (rect.left + rect.width / 2) + 'px';
    tooltip.style.top = (rect.top - 8) + 'px';
    tooltip.style.transform = 'translateX(-50%) translateY(-100%)';

    setTimeout(() => tooltip.style.opacity = '1', 10);

    this.currentTooltip = tooltip;
  }

  hideTooltip() {
    if (this.currentTooltip) {
      this.currentTooltip.remove();
      this.currentTooltip = null;
    }
  }

  getCartTotal() {
    // Helper to get current cart total for analytics
    return parseFloat(document.querySelector('.total-row:not(.estimate) .total-amount')?.textContent.replace('$', '') || '0');
  }

  getCartData() {
    // Helper to get cart data for analytics
    // This would need to be connected to the actual cart data from the main script
    // For now, return a basic structure
    return window.cart || { items: [] };
  }

  getMealCount(cartData) {
    return cartData.items?.reduce((sum, item) => {
      const isMeal = item.properties && item.properties.meal_item === "1";
      return isMeal ? sum + item.quantity : sum;
    }, 0) || 0;
  }

  getPackageCount(cartData) {
    return cartData.items?.reduce((sum, item) => {
      const isPackage = item.properties && item.properties.package_item === "1";
      return isPackage ? sum + item.quantity : sum;
    }, 0) || 0;
  }

  getByPoundCount(cartData) {
    return cartData.items?.reduce((sum, item) => {
      const isByPound = item.properties && item.properties.by_pound_item === "1";
      return isByPound ? sum + item.quantity : sum;
    }, 0) || 0;
  }

  setupPaginationObserver() {
    // Enhanced pagination with better tracking
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.loadingMore) {
          const gridElement = entry.target.closest('.meals-grid');
          const tabContent = gridElement?.closest('.tab-content');

          // Only load more if this tab is active and has a collection handle
          if (tabContent && tabContent.classList.contains('active') && gridElement?.dataset.collectionHandle) {
            this.trackPaginationEvent(gridElement);
          }
        }
      });
    }, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    });

    // Observe loading indicators when they become available
    const observeLoadingIndicators = () => {
      const grids = document.querySelectorAll('.meals-grid[data-collection-handle]');
      grids.forEach(grid => {
        const loadingIndicator = grid.querySelector('.loading-indicator');
        if (loadingIndicator && !loadingIndicator.dataset.observed) {
          observer.observe(loadingIndicator);
          loadingIndicator.dataset.observed = 'true';
        }
      });
    };

    // Initial observation
    observeLoadingIndicators();

    // Re-observe when tab changes
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('main-tab')) {
        setTimeout(observeLoadingIndicators, 100);
      }
    });
  }

  trackPaginationEvent(gridElement) {
    // Track pagination analytics
    const currentPage = parseInt(gridElement.dataset.currentPage) || 1;
    const collectionHandle = gridElement.dataset.collectionHandle;
    const gridType = gridElement.id.replace('-grid', '');

    // Track with analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', 'meal_builder_pagination', {
        collection: collectionHandle,
        grid_type: gridType,
        page: currentPage + 1,
        source: 'infinite_scroll'
      });
    }

    if (window.ShopifyAnalytics && typeof window.ShopifyAnalytics.trackCustomEvent === 'function') {
      window.ShopifyAnalytics.trackCustomEvent('meal_builder_pagination', {
        collection: collectionHandle,
        grid_type: gridType,
        page: currentPage + 1
      });
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new MealBuilder();
});

// Export for potential external use
window.MealBuilder = MealBuilder;
